"use client";

import { clearTableResponses } from "@/lib/api/table-response-cache";
import { clearSupplyPlanSnapshotCache } from "@/lib/client/supply-plan-snapshot-cache";
import { useState, useMemo, useRef, useEffect, useLayoutEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import { Sun, Moon } from "lucide-react";
import { useHeader } from "@/app/context/HeaderContext";
import { useData } from "@/app/context/DataContext";
import { useDataScope } from "@/app/context/DataScopeContext";
import { useDataHealth } from "@/app/components/useDataHealth";
import { useRefresh, useRefreshScopeStatus } from "@/app/context/RefreshContext";
import { useStore } from "@/app/context/StoreContext";
import { ATTR_FILTER_OPTIONS, NUMERIC_FILTER_OPTIONS } from "@/lib/constants";
import { getColLabel } from "@/lib/columns";
import { COLUMN_FILTER_MODES } from "@/lib/filtering";
import { LATEST_WHATS_NEW_VERSION, WHATS_NEW_SEEN_KEY, WHATS_NEW_SEEN_EVENT } from "@/lib/whats-new-version";
import { useExitAnimation } from "@/app/hooks/useExitAnimation";
import AttrDropdown from "./header/AttrDropdown";
import FilterPanel from "./header/FilterPanel";
import { ADMIN_PATH, LEGACY_ADMIN_PATH, LEGACY_HOME_PATH } from "@/lib/app-routes";

const SORT_OPTIONS: { key: string | null; label: string; defaultDir: "asc" | "desc" }[] = [
  { key: null,         label: "По умолчанию", defaultDir: "desc" },
  { key: "orders_30d", label: "Заказы 30д",   defaultDir: "desc" },
  { key: "orders_14d", label: "Заказы 14д",   defaultDir: "desc" },
  { key: "orders_7d",  label: "Заказы 7д",    defaultDir: "desc" },
  { key: "revenue",    label: "Выручка",       defaultDir: "desc" },
  { key: "stock",      label: "Остаток",       defaultDir: "asc"  },
  { key: "price",      label: "Цена",          defaultDir: "desc" },
];

const QUICK_ATTR = ["attr_season", "attr_brand", "type_name"];
const REFRESH_DISABLED_REASON = "В режиме «Все магазины» обновление недоступно. Выберите конкретный магазин.";
const HEADER_META_DELAY_MS = 1_500;
const VIEW_HEADER_META_DELAY_MS = 30_000;
const WORKSPACE_TOPBAR_HEIGHT = 27;
const FULL_TOPBAR_INITIAL_HEIGHT = 116;
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

function RefreshSpinner({ className = "" }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block h-3 w-3 rounded-full border-2 border-current border-r-transparent align-[-2px] animate-spin ${className}`}
    />
  );
}

function RefreshDisabledTooltip({ align = "left" }: { align?: "left" | "right" }) {
  return (
    <span
      className={`pointer-events-none absolute top-full z-[80] mt-1 w-64 rounded-lg border border-gray-100 bg-white px-3 py-2 text-left text-xs leading-snug text-gray-600 opacity-0 shadow-lg transition-opacity group-hover/refreshhint:opacity-100 group-focus-within/refreshhint:opacity-100 ${align === "right" ? "right-0" : "left-0"}`}
    >
      {REFRESH_DISABLED_REASON}
    </span>
  );
}

function formatRefreshTime(timestamp: number | null) {
  if (!timestamp) return "ещё не запускалось";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

function formatRefreshDuration(ms: number | null) {
  if (!ms) return null;
  if (ms < 60_000) return `${Math.max(1, Math.round(ms / 1000))} с`;
  return `${Math.floor(ms / 60_000)} мин ${Math.round((ms % 60_000) / 1000)} с`;
}

export default function AppHeader() {
  const pathname = usePathname();
  // compactOnly — только зелёная системная полоса, без строки поиска/фильтров:
  // на этих страницах поиск и фильтры таблиц не имеют смысла.
  if (pathname === LEGACY_HOME_PATH) return <FullAppHeader pathname={pathname} compactOnly />; // старая сводка: поиск/фильтры не нужны
  if (pathname.startsWith("/ab-tests")) return <FullAppHeader pathname={pathname} compactOnly />;
  if (pathname.startsWith("/event-feed")) return <FullAppHeader pathname={pathname} compactOnly />;
  if (pathname.startsWith("/assortment-map")) return <FullAppHeader pathname={pathname} compactOnly />;
  if (pathname.startsWith("/supply-pult")) return <FullAppHeader pathname={pathname} compactOnly />;
  return <FullAppHeader pathname={pathname} />;
}

function FullAppHeader({ pathname, compactOnly = false }: { pathname: string; compactOnly?: boolean }) {
  const router = useRouter();
  const {
    user: currentUser,
    impersonating,
    mode: storeMode,
    store: activeStoreObj,
    stores,
  } = useStore();
  const isAdmin = currentUser?.role === "admin";
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const [hasUnreadWhatsNew, setHasUnreadWhatsNew] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const settingsMenu = useExitAnimation(settingsMenuOpen);
  useEffect(() => {
    const update = () => {
      try {
        setHasUnreadWhatsNew((localStorage.getItem(WHATS_NEW_SEEN_KEY) ?? "") !== LATEST_WHATS_NEW_VERSION);
      } catch {
        setHasUnreadWhatsNew(false);
      }
    };
    update();
    window.addEventListener("storage", update);
    window.addEventListener(WHATS_NEW_SEEN_EVENT, update);
    return () => {
      window.removeEventListener("storage", update);
      window.removeEventListener(WHATS_NEW_SEEN_EVENT, update);
    };
  }, []);

  const handleLogout = async () => {
    await fetch("/api/auth/logout", { method: "POST" });
    await Promise.all([clearTableResponses(), clearSupplyPlanSnapshotCache()]);
    router.push("/login");
    router.refresh();
  };

  const handleStopImpersonation = async () => {
    const res = await fetch("/api/admin/impersonate/stop", { method: "POST" });
    if (!res.ok) return;
    await clearTableResponses();
    localStorage.clear();
    try {
      const dbs = await indexedDB.databases?.();
      if (dbs) for (const db of dbs) if (db.name) indexedDB.deleteDatabase(db.name);
    } catch {}
    window.location.href = ADMIN_PATH;
  };

  const {
    search, setSearch,
    attrFilters, setAttrFilter,
    numFilters, setNumFilter, clearNumFilter,
    columnFilters, clearColumnFilter,
    clearAllFilters,
    sortKey, sortDir, setSort,
    hasNoteFilter, setHasNoteFilter,
    noteOfferIds, setNoteOfferIds,
    hasFavFilter, setHasFavFilter, onFavFilterToggle,
    promoFilter, setPromoFilter, promoFilterVisible,
    filteredCount, totalCount,
    storeScopeControl,
  } = useHeader();

  const { scopeQuery, scopeReady } = useDataScope();
  const {
    startRefresh,
    status: localRefreshStatus,
    hasErrors: localHasErrors,
    steps: refreshSteps,
    durationMs: refreshDurationMs,
    lastJobAt,
    openModal,
  } = useRefresh();
  const isProductWorkspace =
    pathname.startsWith("/view/") ||
    pathname.startsWith("/templates/preview/");
  const refreshScope = useRefreshScopeStatus(scopeQuery, scopeReady);
  // «Здоровье данных»: то, о чём обновление молчит — отставшие источники, дыры
  // в истории, отвалившиеся свои таблицы, неработающие ключи (lib/data-health).
  const dataHealth = useDataHealth(scopeQuery, scopeReady);
  const refreshStatus = refreshScope.data?.aggregate.status ?? localRefreshStatus;
  const isRefreshing = localRefreshStatus === "running" || refreshStatus === "running";
  const hasRefreshProblems = localHasErrors || refreshScope.data?.aggregate.hasErrors || refreshStatus === "warn";
  const hasDataProblems = dataHealth.issues.length > 0;
  const hasProblems = Boolean(hasRefreshProblems) || hasDataProblems;
  const scopedRefreshStoreId = isProductWorkspace &&
    storeScopeControl?.storeScope === "selected" &&
    storeScopeControl.selectedStoreIds.length === 1
      ? storeScopeControl.selectedStoreIds[0]
      : null;
  const scopedRefreshStoreName = scopedRefreshStoreId
    ? stores.find(store => store.id === scopedRefreshStoreId)?.name ?? "магазин вида"
    : null;
  // В режиме «Все магазины» полный refresh всё ещё невозможен, но если страница
  // вида закреплена за одним магазином, обновляем именно этот магазин.
  const refreshDisabled = storeMode === "all" && !scopedRefreshStoreId;
  const headerMetaDelay = isProductWorkspace ? VIEW_HEADER_META_DELAY_MS : HEADER_META_DELAY_MS;

  useEffect(() => {
    if (isProductWorkspace) return;
    setNoteOfferIds(new Set());
    setHasNoteFilter(false);
  }, [isProductWorkspace, setHasNoteFilter, setNoteOfferIds]);
  const { attrData, products } = useData();

  // Себестоимость: статистика для индикатора-треугольника рядом с лейблом.
  // На страницах вида берём тот же scope магазинов, что и сама таблица. Иначе
  // вид с выбранными магазинами мог показывать таблицу по одному магазину, а
  // предупреждение "Себестоимость" в шапке считалось по глобальному режиму.
  const [costStats, setCostStats] = useState<{ active: number; filledActive: number; emptyActive: number } | null>(null);
  useEffect(() => {
    if (!isProductWorkspace || !scopeReady) {
      setCostStats(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams(scopeQuery.startsWith("?") ? scopeQuery.slice(1) : scopeQuery);
      params.set("stats", "1");
      fetch(`/api/cost-prices?${params.toString()}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (!cancelled) setCostStats(d?.stats ?? null); })
        .catch(() => {});
    }, headerMetaDelay);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [headerMetaDelay, isProductWorkspace, scopeQuery, scopeReady]);

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const sortDropdown = useExitAnimation(openDropdown === "sort");
  const storesDropdown = useExitAnimation(openDropdown === "stores");
  const filterPanelDropdown = useExitAnimation(openDropdown === "filterPanel");
  const [periodHintOpen, setPeriodHintOpen] = useState(false);
  const periodHint = useExitAnimation(periodHintOpen);
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);

  useIsomorphicLayoutEffect(() => {
    if (!headerRef.current) return;
    const update = () => {
      if (headerRef.current)
        document.documentElement.style.setProperty("--topbar-h", `${headerRef.current.offsetHeight}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(headerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      // Во время горизонтального хвата (useDragToPan ставит флаг) не читаем scrollY —
      // это чтение во время JS-скролла форсило reflow и давало «чуть тормозит». Хват
      // горизонтальный, шапку прятать всё равно не нужно. Вертикальный скролл флага не
      // ставит → авто-скрытие работает.
      if (document.documentElement.classList.contains("view-grab-active")) return;
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastScrollY.current;
        if (delta > 8 && y > 80) setHeaderVisible(false);
        else if (delta < -5) setHeaderVisible(true);
        lastScrollY.current = y;
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // CSS-переменная `--topbar-offset` — актуальный верхний оффсет для sticky
  // элементов (шапки таблиц, и т.п.). Когда топбар скрыт по скроллу —
  // становится 0, чтобы sticky-headers прилипали к самому верху окна, а не
  // висели там где раньше был топбар (оставляя пустое место с прокрученной
  // таблицей над ними).
  useIsomorphicLayoutEffect(() => {
    if (!headerRef.current) return;
    const h = headerRef.current.offsetHeight;
    const offset = headerVisible ? `${h}px` : "0px";
    const rootStyle = document.documentElement.style;
    rootStyle.setProperty("--topbar-offset", offset);
    return () => {
      // /page использует собственную шапку высотой 44px. Без очистки после
      // клиентского перехода там оставался отступ старой шапки (до 116px), и
      // закреплённая строка таблицы висела заметно ниже нужного места.
      if (rootStyle.getPropertyValue("--topbar-offset") === offset) {
        rootStyle.removeProperty("--topbar-offset");
      }
    };
  }, [headerVisible]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpenDropdown(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!periodHintOpen) return;
    const timer = window.setTimeout(() => setPeriodHintOpen(false), 1800);
    return () => window.clearTimeout(timer);
  }, [periodHintOpen]);

  const attrValues = useMemo(() => {
    const result: Record<string, string[]> = {};
    for (const opt of ATTR_FILTER_OPTIONS) {
      if (opt.value === "type_name") {
        const set = new Set<string>();
        for (const p of products) { if (p.type_name) set.add(p.type_name); }
        result["type_name"] = Array.from(set).sort();
        continue;
      }
      const set = new Set<string>();
      for (const attrs of Object.values(attrData)) {
        for (const id of opt.ids) { if (attrs[id]) set.add(attrs[id]); }
      }
      result[opt.value] = Array.from(set).sort();
    }
    return result;
  }, [attrData, products]);

  const toggleAttrValue = (key: string, val: string) => {
    const current = attrFilters[key] ?? [];
    setAttrFilter(key, current.includes(val) ? current.filter(v => v !== val) : [...current, val]);
  };

  // ── Quick-action (настраиваемая кнопка справа от поиска) ──
  // Тип действия хранится в /api/user-prefs (user-specific). Клик по основной
  // части кнопки выполняет действие, клик по ▾ открывает меню выбора.
  // Текущее поведение: по умолчанию "refresh", alternative — "create" (открыть
  // редактор нового вида через глобальный event для Sidebar).
  const [quickAction, setQuickActionState] = useState<"refresh" | "create">("refresh");
  const [quickMenuOpen, setQuickMenuOpen] = useState(false);
  const quickMenu = useExitAnimation(quickMenuOpen);
  const quickMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetch("/api/user-prefs")
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          const qa = d?.prefs?.quickAction;
          if (qa === "refresh" || qa === "create") setQuickActionState(qa);
        })
        .catch(err => console.error("[AppHeader] user-prefs load:", err));
    }, headerMetaDelay);
    return () => window.clearTimeout(timer);
  }, [headerMetaDelay, pathname]);

  useEffect(() => {
    if (!quickMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!quickMenuRef.current?.contains(e.target as Node)) setQuickMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [quickMenuOpen]);

  const setQuickAction = (next: "refresh" | "create") => {
    setQuickActionState(next);
    fetch("/api/user-prefs", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ quickAction: next }),
    }).catch(err => console.error("[AppHeader] user-prefs save:", err));
  };

  const executeQuickAction = (action: "refresh" | "create") => {
    if (action === "refresh") {
      if (refreshDisabled) return;
      setRefreshMenuOpen(false);
      setRefreshInfoOpen(true);
      startRefresh(undefined, scopedRefreshStoreId);
    } else if (action === "create") {
      // Sidebar.tsx слушает это событие и открывает единое меню создания.
      window.dispatchEvent(new Event("frogstat:new-view"));
    }
  };

  const handleRefresh = () => {
    if (refreshDisabled) return;
    startRefresh(undefined, scopedRefreshStoreId);
  };

  // Обновление выполняется в фоне. По основной кнопке открывается компактный
  // статус рядом с ней, а полный журнал — только по отдельной ссылке внутри.
  const [refreshInfoOpen, setRefreshInfoOpen] = useState(false);
  const refreshInfo = useExitAnimation(refreshInfoOpen);
  const [refreshMenuOpen, setRefreshMenuOpen] = useState(false);
  const refreshMenu = useExitAnimation(refreshMenuOpen);
  const refreshControlsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!refreshMenuOpen && !refreshInfoOpen) return;
    const close = (e: MouseEvent) => {
      if (!refreshControlsRef.current?.contains(e.target as Node)) {
        setRefreshMenuOpen(false);
        setRefreshInfoOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [refreshInfoOpen, refreshMenuOpen]);
  useEffect(() => {
    if (refreshDisabled) {
      setRefreshMenuOpen(false);
      setRefreshInfoOpen(false);
    }
  }, [refreshDisabled]);

  const refreshIssues = refreshSteps.filter(step => step.status === "error" || step.status === "warn");
  // Шаги последнего обновления панель показывает подробно сама — не повторяем
  // их сводкой из проверки данных, когда эти шаги уже на экране.
  const healthIssues = refreshIssues.length > 0
    ? dataHealth.issues.filter(issue => issue.kind !== "refresh_failed" && issue.kind !== "refresh_late")
    : dataHealth.issues;
  const completedRefreshSteps = refreshSteps.filter(step => step.status === "done").length;
  const refreshStatusTitle = isRefreshing
    ? "Обновление идёт"
    : refreshStatus === "auth_error"
      ? "Нужно обновить ключи Ozon"
      : refreshStatus === "error"
        ? "Обновление не завершилось"
        : hasRefreshProblems
          ? "Обновление с замечаниями"
          : refreshSteps.length > 0
            ? "Данные обновлены"
            : "Обновление данных";

  const toggle = (key: string) => setOpenDropdown(v => v === key ? null : key);

  const activeSort = SORT_OPTIONS.find(o => o.key === sortKey);
  const activeSortLabel = activeSort
    ? activeSort.label
    : sortKey
      ? (getColLabel(sortKey) || sortKey)
      : null;
  const hasFilters = Object.keys(attrFilters).length > 0 || numFilters.length > 0 || columnFilters.length > 0 || hasNoteFilter;
  const searchPlaceholder = pathname.startsWith("/ads")
    ? "Поиск по кампании, товару или ID"
    : "Быстрый поиск / название, артикул или SKU";

  const extraAttrChips = Object.entries(attrFilters)
    .filter(([k]) => !QUICK_ATTR.includes(k))
    .flatMap(([k, vals]) => {
      const opt = ATTR_FILTER_OPTIONS.find(o => o.value === k);
      return vals.map(v => ({ key: k, val: v, label: `${opt?.label ?? k}: ${v}` }));
    });

  const numChips = numFilters.map(f => {
    const opt = NUMERIC_FILTER_OPTIONS.find(o => o.value === f.key);
    const parts = [];
    if (f.min != null) parts.push(`≥${f.min}`);
    if (f.max != null) parts.push(`≤${f.max}`);
    return { key: f.key, label: `${opt?.label ?? f.key} ${parts.join(" ")}` };
  });
  const columnChips = columnFilters.map(filter => {
    const modeLabel = COLUMN_FILTER_MODES.find(mode => mode.value === filter.mode)?.label ?? "фильтр";
    const valueSelection = Array.isArray(filter.selectedValues)
      ? filter.selectedValues.length > 0 ? `, выбрано: ${filter.selectedValues.length}` : ", нет выбранных значений"
      : Array.isArray(filter.excludedValues) && filter.excludedValues.length > 0 ? `, исключено: ${filter.excludedValues.length}` : "";
    const condition = filter.mode === "number_between"
      ? [filter.min ? `от ${filter.min}` : "", filter.max ? `до ${filter.max}` : ""].filter(Boolean).join(" ") || modeLabel
      : (filter.mode.startsWith("number_") || filter.mode.startsWith("text_")) && filter.value
        ? `${modeLabel}: ${filter.value}`
        : modeLabel;
    return { key: filter.key, label: `${getColLabel(filter.key) || filter.key}: ${condition}${valueSelection}` };
  });
  const showStoreFilter = pathname.startsWith("/view/") && !!storeScopeControl && stores.length > 1;
  const sidebarStoreIds = useMemo(
    () => storeMode === "all" ? stores.map(store => store.id) : activeStoreObj?.id ? [activeStoreObj.id] : [],
    [activeStoreObj?.id, storeMode, stores],
  );
  const selectedStoreSet = useMemo(() => new Set(storeScopeControl?.selectedStoreIds ?? []), [storeScopeControl?.selectedStoreIds]);
  const effectiveStoreSet = useMemo(() => {
    if (!storeScopeControl || storeScopeControl.storeScope === "current") return new Set(sidebarStoreIds);
    if (storeScopeControl.storeScope === "all") return new Set(stores.map(store => store.id));
    return selectedStoreSet;
  }, [selectedStoreSet, sidebarStoreIds, storeScopeControl, stores]);
  const selectedStoreNames = useMemo(
    () => stores.filter(store => effectiveStoreSet.has(store.id)).map(store => store.name),
    [stores, effectiveStoreSet],
  );
  const storeScopeLabel = !storeScopeControl || storeScopeControl.storeScope === "current"
    ? (storeMode === "all" ? "Все магазины" : activeStoreObj?.name ?? "Магазин")
    : storeScopeControl.storeScope === "all"
      ? "Все магазины"
      : selectedStoreNames.length === stores.length
        ? "Все магазины"
        : selectedStoreNames.length === 0
        ? "Магазины"
        : selectedStoreNames.length === 1
          ? selectedStoreNames[0]
          : `Магазины: ${selectedStoreNames.length}`;
  const storeScopeButtonLabel = storeScopeControl?.storeScope === "current"
    ? `Текущий: ${storeScopeLabel}`
    : `Закреплено: ${storeScopeLabel}`;
  const storeScopeModeLabel = storeScopeControl?.storeScope === "current"
    ? "Следует за нижним переключателем"
    : storeScopeControl?.storeScope === "all"
      ? "Закреплено: все магазины"
      : "Закреплено за страницей";
  const toggleHeaderStore = (storeId: string) => {
    if (!storeScopeControl) return;
    const base = storeScopeControl.storeScope === "all"
      ? stores.map(store => store.id)
      : storeScopeControl.storeScope === "selected"
        ? storeScopeControl.selectedStoreIds
        : sidebarStoreIds;
    if (storeScopeControl.storeScope === "selected" && base.length === 1 && !base.includes(storeId)) {
      storeScopeControl.onChange("selected", [storeId]);
      return;
    }
    const next = base.includes(storeId) ? base.filter(id => id !== storeId) : [...base, storeId];
    if (next.length === 0) {
      storeScopeControl.onChange("current", []);
      return;
    }
    storeScopeControl.onChange("selected", next);
  };

  return (
    <div
      ref={headerRef}
      className="fixed top-0 right-0 z-30"
      style={{
        left: "var(--sidebar-w, 220px)",
        backdropFilter: "blur(9px)",
        WebkitBackdropFilter: "blur(9px)",
        transform: headerVisible ? "translateY(0)" : "translateY(-100%)",
        transition: "left var(--sidebar-motion-ms, 300ms) cubic-bezier(0.16, 1, 0.3, 1), transform 0.3s ease",
        background: compactOnly ? "var(--topbar-bg)" : "linear-gradient(to bottom, var(--header-bg-from) 0%, var(--header-bg-from) 50%, var(--header-bg-to) 100%)",
        boxShadow: "0 1px 0 var(--header-shadow)",
      }}
    >
      <style>{`:root{--topbar-h:${compactOnly ? WORKSPACE_TOPBAR_HEIGHT : FULL_TOPBAR_INITIAL_HEIGHT}px;--topbar-offset:${compactOnly ? WORKSPACE_TOPBAR_HEIGHT : FULL_TOPBAR_INITIAL_HEIGHT}px;}`}</style>
      {/* System top bar */}
      <div style={{ background: "var(--topbar-bg)", borderBottom: "1px solid var(--topbar-border)", height: WORKSPACE_TOPBAR_HEIGHT }}>
        <div className="max-w-[1140px] mx-auto h-full flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Кнопка обновления, статус и выбор частичного refresh. Основной
                сценарий не открывает центральную модалку и не затемняет страницу. */}
            <div ref={refreshControlsRef} className="relative flex items-center">
              <span className="group/refreshhint relative inline-flex">
                <button
                  onClick={() => {
                    if (isRefreshing) {
                      setRefreshInfoOpen(v => !v);
                      return;
                    }
                    setRefreshMenuOpen(false);
                    setRefreshInfoOpen(true);
                    handleRefresh();
                  }}
                  disabled={refreshDisabled && !isRefreshing}
                  className="text-xs hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
                  style={{ color: "var(--topbar-text)" }}
                  title={isRefreshing ? "Показать ход обновления" : scopedRefreshStoreName ? `Обновить ${scopedRefreshStoreName}` : undefined}
                  aria-expanded={refreshInfoOpen}
                >
                  {isRefreshing ? <span className="inline-flex items-center gap-1.5"><RefreshSpinner />Обновляется...</span> : "Обновить"}
                </button>
                {refreshDisabled && !isRefreshing && <RefreshDisabledTooltip />}
              </span>
              {hasProblems && !isRefreshing && (
                <button
                  onClick={() => {
                    setRefreshMenuOpen(false);
                    setRefreshInfoOpen(v => !v);
                  }}
                  className={`-ml-0.5 flex h-4 w-4 items-center justify-center rounded text-[11px] ${dataHealth.severity === "error" ? "text-red-500 hover:bg-red-50 hover:text-red-700" : "text-amber-500 hover:bg-amber-50 hover:text-amber-700"}`}
                  title={healthIssues[0]?.title ?? "Показать замечания последнего обновления"}
                  aria-label="Показать, что с данными не так"
                  aria-expanded={refreshInfoOpen}
                >
                  ⚠
                </button>
              )}
              {mounted && (
                <>
                  <button
                    onClick={() => {
                      if (refreshDisabled) return;
                      setRefreshMenuOpen(v => !v);
                    }}
                    disabled={isRefreshing || refreshDisabled}
                    className="ml-1 text-[10px] px-1 hover:opacity-70 disabled:opacity-50"
                    style={{ color: "var(--topbar-text)" }}
                    title="Обновить только часть данных"
                  >
                    ▾
                  </button>
                  {refreshMenu.mounted && (
                    <div
                      className="ui-popover-panel ui-popover-panel-left ui-dropdown-panel absolute left-0 top-full mt-1 z-50 w-44"
                      data-state={refreshMenu.state}
                    >
                      {[
                        { ids: undefined,        label: "Всё (быстро)" },
                        { ids: undefined,        label: "Глубокая сверка", includeDailyAnalytics: true },
                        { ids: ["orders"],       label: "Только заказы" },
                        { ids: ["prices"],       label: "Только цены" },
                        { ids: ["stocks", "clusters", "supplies"], label: "Только остатки" },
                        { ids: ["finances"],     label: "Только финансы" },
                        { ids: ["performance"],  label: "Только реклама" },
                        { ids: ["supplies"],     label: "Только поставки" },
                        { ids: ["promotions"],   label: "Только акции" },
                      ].map(opt => (
                        <button
                          key={opt.label}
                          onClick={() => {
                            if (refreshDisabled) return;
                            setRefreshMenuOpen(false);
                            setRefreshInfoOpen(true);
                            startRefresh(opt.ids, scopedRefreshStoreId, {
                              includeDailyAnalytics: opt.includeDailyAnalytics,
                            });
                          }}
                          disabled={refreshDisabled}
                          className="ui-dropdown-item disabled:text-gray-300"
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
              {refreshInfo.mounted && (
                <div
                  className="ui-popover-panel ui-popover-panel-left absolute left-0 top-full z-[60] mt-1 w-[340px] overflow-hidden"
                  data-state={refreshInfo.state}
                  role="dialog"
                  aria-label="Сведения об обновлении"
                >
                  <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-3 py-2.5">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
                        {isRefreshing ? <RefreshSpinner className="text-blue-600" /> : hasRefreshProblems ? <span className="text-yellow-500">⚠</span> : refreshSteps.length > 0 ? <span className="text-green-600">✓</span> : <span className="text-gray-400">↻</span>}
                        <span>{refreshStatusTitle}</span>
                      </div>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {isRefreshing
                          ? refreshSteps.length > 0 ? `Готово ${completedRefreshSteps} из ${refreshSteps.length} этапов` : "Подключаемся к обновлению…"
                          : lastJobAt ? `Последнее: ${formatRefreshTime(lastJobAt)}${formatRefreshDuration(refreshDurationMs) ? ` · ${formatRefreshDuration(refreshDurationMs)}` : ""}` : "Запустите обновление, чтобы получить свежие данные"}
                      </p>
                    </div>
                    <button
                      onClick={() => setRefreshInfoOpen(false)}
                      className="-mr-1 -mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded text-lg leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                      title="Закрыть"
                      aria-label="Закрыть сведения об обновлении"
                    >
                      ×
                    </button>
                  </div>

                  <div className="max-h-52 overflow-y-auto px-3 py-2.5">
                    {healthIssues.length > 0 && (
                      <div className="mb-2 space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">С данными</p>
                        {healthIssues.map(issue => (
                          <div key={issue.id} className={`rounded-md px-2 py-1.5 text-xs ${issue.severity === "error" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>
                            <div className="flex items-start gap-1.5">
                              <span className="mt-px shrink-0 font-semibold">{issue.severity === "error" ? "✗" : "⚠"}</span>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium">{issue.title}</p>
                                <p className="mt-0.5 break-words text-[11px] leading-snug opacity-90">{issue.detail}</p>
                                {issue.action?.kind === "refresh" && (
                                  <button
                                    onClick={() => { setRefreshInfoOpen(false); void startRefresh(undefined, issue.action?.kind === "refresh" ? issue.action.storeId : null, { openModal: true }); }}
                                    className="mt-1 rounded bg-white/70 px-1.5 py-0.5 text-[11px] font-medium hover:bg-white"
                                  >
                                    {issue.action.label}
                                  </button>
                                )}
                                {(issue.action?.kind === "keys" || issue.action?.kind === "sources" || issue.action?.kind === "details") && (
                                  <Link
                                    href={issue.action.kind === "keys" ? "/settings?tab=stores" : issue.action.kind === "sources" ? "/settings?tab=sources" : "/settings?tab=health"}
                                    prefetch={false}
                                    onClick={() => setRefreshInfoOpen(false)}
                                    className="mt-1 inline-block rounded bg-white/70 px-1.5 py-0.5 text-[11px] font-medium hover:bg-white"
                                  >
                                    {issue.action.label}
                                  </Link>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {refreshIssues.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                          {refreshIssues.some(step => step.status === "error") ? "Не обновилось / требует внимания" : "Требует внимания"}
                        </p>
                        {refreshIssues.map(step => (
                          <div key={step.stepId} className={`rounded-md px-2 py-1.5 text-xs ${step.status === "error" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>
                            <div className="flex items-start gap-1.5">
                              <span className="mt-px shrink-0 font-semibold">{step.status === "error" ? "✗" : "⚠"}</span>
                              <div className="min-w-0">
                                <p className="font-medium">{step.label}</p>
                                {step.error && <p className="mt-0.5 break-words text-[11px] leading-snug opacity-90">{step.error}</p>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : isRefreshing ? (
                      <p className="text-xs leading-relaxed text-gray-600">Данные обновляются в фоне. Можно продолжать работать — это окно будет показывать ход выполнения.</p>
                    ) : healthIssues.length > 0 ? null : hasRefreshProblems ? (
                      <p className="text-xs leading-relaxed text-gray-600">Последнее обновление завершилось с замечаниями. Откройте журнал, чтобы увидеть все этапы.</p>
                    ) : refreshSteps.length > 0 ? (
                      <p className="text-xs leading-relaxed text-gray-600">Все этапы завершились успешно. Свежие данные уже используются в сервисе.</p>
                    ) : (
                      <p className="text-xs leading-relaxed text-gray-500">Здесь появятся время, результат и причины, если какой-то этап не обновится.</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/70 px-3 py-2">
                    <Link
                      href="/settings?tab=health"
                      prefetch={false}
                      onClick={() => setRefreshInfoOpen(false)}
                      className="text-xs font-medium text-gray-500 hover:text-gray-800 hover:underline"
                    >
                      Здоровье данных
                    </Link>
                    {refreshSteps.length > 0 && (
                      <button
                        onClick={() => {
                          setRefreshInfoOpen(false);
                          openModal();
                        }}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        Полный журнал
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <Link href="/notes" prefetch={false} className="text-xs hover:underline" style={{ color: "var(--topbar-text)" }}>Заметки</Link>
            <Link
              href="/v1/cost"
              prefetch={false}
              className="text-xs hover:underline inline-flex items-center gap-0.5"
              style={{ color: "var(--topbar-text)" }}
              title={costStats ? `Заполнено ${costStats.filledActive} из ${costStats.active} активных${costStats.emptyActive > 0 ? ` (${costStats.emptyActive} пустых)` : ""}` : "Себестоимость товаров"}
            >
              <span>₽ Себестоимость</span>
              {costStats && costStats.emptyActive > 0 && (
                <span className="text-orange-400" aria-label="Есть незаполненные">⚠</span>
              )}
            </Link>
            <Link href="/product-groups" prefetch={false} className="text-xs hover:underline" style={{ color: "var(--topbar-text)" }}>
              Склейки
            </Link>
            <Link
              href="/whats-new"
              prefetch={false}
              className="relative inline-flex items-center text-xs hover:underline"
              style={{ color: "var(--topbar-text)" }}
            >
              Что нового
              {mounted && hasUnreadWhatsNew && pathname !== "/whats-new" && (
                <span className="absolute -right-1.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-red-500" aria-label="Есть новые изменения" />
              )}
            </Link>
          </div>
          <div className="flex items-center gap-4">
            {mounted && (
              <button
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="flex items-center justify-center w-5 h-5 rounded hover:opacity-70 transition-opacity"
                style={{ color: "var(--topbar-text)" }}
                title={theme === "dark" ? "Светлая тема" : "Тёмная тема"}
              >
                {theme === "dark" ? <Sun size={12} /> : <Moon size={12} />}
              </button>
            )}
            {isAdmin && <Link href={LEGACY_ADMIN_PATH} prefetch={false} className="text-xs hover:underline" style={{ color: "var(--topbar-text)" }}>Админка</Link>}
            {impersonating && (
              <button
                onClick={handleStopImpersonation}
                className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100"
                type="button"
              >
                Вернуться в админа
              </button>
            )}
            {currentUser && (
              <div
                className="relative flex items-center py-1"
                onMouseEnter={() => setSettingsMenuOpen(true)}
                onMouseLeave={() => setSettingsMenuOpen(false)}
              >
                <Link href="/settings" prefetch={false} className="text-xs hover:underline" style={{ color: "var(--topbar-text)" }}>
                  Настройки
                </Link>
                <button
                  type="button"
                  onClick={() => setSettingsMenuOpen(v => !v)}
                  className="ml-1 text-[10px] px-1 hover:opacity-70"
                  style={{ color: "var(--topbar-text)" }}
                  title="Разделы настроек"
                >
                  ▾
                </button>
                {settingsMenu.mounted && (
                  <div className="ui-popover-panel absolute right-0 top-full z-50 w-40 pt-3" data-state={settingsMenu.state}>
                    <div className="absolute right-5 top-[5px] h-0 w-0 border-x-[8px] border-b-[8px] border-x-transparent drop-shadow-[0_-1px_2px_rgba(0,0,0,0.10)]" style={{ borderBottomColor: "var(--dropdown-border)" }} />
                    <div className="absolute right-5 top-[6px] h-0 w-0 border-x-[8px] border-b-[8px] border-x-transparent" style={{ borderBottomColor: "var(--dropdown-bg)" }} />
                    <div className="ui-dropdown-panel relative z-0">
                      {[
                        { href: "/settings?tab=stores", label: "Подключение" },
                        { href: "/settings?tab=employees", label: "Сотрудники" },
                        { href: "/settings?tab=account", label: "Профиль" },
                      ].map(item => (
                        <Link
                          key={item.href}
                          href={item.href}
                          prefetch={false}
                          onClick={() => setSettingsMenuOpen(false)}
                          className="ui-dropdown-item"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {currentUser && (
              <span className="text-xs max-w-[160px] truncate" style={{ color: "var(--topbar-text)" }} title={currentUser.email ?? undefined}>
                {currentUser.email ?? "Пользователь"}
              </span>
            )}
            <button onClick={handleLogout} className="text-xs hover:underline" style={{ color: "var(--topbar-text)" }}>Выйти</button>
          </div>
        </div>
      </div>

      {!compactOnly && (
      <div ref={containerRef} className="max-w-[1140px] mx-auto">

        {/* Row 1: search + refresh */}
        <div className="flex items-center gap-3 py-2.5">
          <div className="flex-1 flex items-center rounded-lg overflow-hidden" style={{ height: 38, background: "var(--header-search-bg)", border: "1px solid var(--header-search-border)" }}>
            <span className="shrink-0 flex items-center justify-center text-sm font-semibold text-gray-800 select-none border-r h-full px-3" style={{ width: 120, borderColor: "var(--header-search-border)" }}>
              🐸 FrogStat
            </span>
            <div className="flex-1 relative h-full">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" width="13" height="13" viewBox="0 0 16 16" fill="none">
                <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                placeholder={searchPlaceholder}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full h-full pl-8 pr-6 text-sm focus:outline-none bg-transparent"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-base leading-none">×</button>
              )}
            </div>
            <button
              onClick={() => searchInputRef.current?.focus()}
              className="shrink-0 flex items-center justify-center text-sm text-gray-600 border-l h-full hover:bg-black/5 transition-colors"
              style={{ width: 70, borderColor: "var(--header-search-border)" }}
            >
              Найти
            </button>
          </div>
          {/* Quick-action — настраиваемая кнопка с двумя режимами. См. logic выше:
              useState quickAction, /api/user-prefs. Юзер кликает ▾ → меняет дефолт. */}
          <div ref={quickMenuRef} className="relative shrink-0 flex" style={{ height: 39 }}>
            <span className="group/refreshhint relative flex">
              <button
                onClick={() => executeQuickAction(quickAction)}
                disabled={(quickAction === "refresh" && (isRefreshing || refreshDisabled))}
                className="flex items-center justify-center gap-1.5 text-sm bg-emerald-600 text-white rounded-l-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                style={{ width: 152, height: 39 }}
              >
                {quickAction === "refresh"
                  ? <>{isRefreshing ? <RefreshSpinner className="text-white" /> : <span>↻</span>} {isRefreshing ? "Обновляется..." : "Обновить"}</>
                  : <><span>+</span> Создать</>}
              </button>
              {quickAction === "refresh" && refreshDisabled && !isRefreshing && <RefreshDisabledTooltip align="right" />}
            </span>
            <button
              onClick={() => setQuickMenuOpen(v => !v)}
              aria-label="Выбрать действие"
              className="flex items-center justify-center text-sm bg-emerald-600 text-white rounded-r-lg hover:bg-emerald-700 transition-colors border-l border-emerald-700/40"
              style={{ width: 30, height: 39 }}
            >
              ▾
            </button>
            {quickMenu.mounted && (
              <div className="ui-popover-panel ui-dropdown-panel absolute right-0 top-full mt-1 z-50 w-52" data-state={quickMenu.state}>
                <button
                  onClick={() => {
                    setQuickAction("refresh");
                    setQuickMenuOpen(false);
                    if (!refreshDisabled) executeQuickAction("refresh");
                  }}
                  disabled={refreshDisabled}
                  className={`ui-dropdown-item py-2 text-sm disabled:text-gray-300 ${quickAction === "refresh" && !refreshDisabled ? "font-semibold" : ""}`}
                >
                  <span>↻</span>
                  <span className="flex-1">Обновить данные</span>
                  {quickAction === "refresh" && <span className="text-xs text-emerald-600">✓</span>}
                </button>
                <button
                  onClick={() => { setQuickAction("create"); setQuickMenuOpen(false); executeQuickAction("create"); }}
                  className={`ui-dropdown-item py-2 text-sm ${quickAction === "create" ? "font-semibold" : ""}`}
                >
                  <span>+</span>
                  <span className="flex-1">Создать вид</span>
                  {quickAction === "create" && <span className="text-xs text-emerald-600">✓</span>}
                </button>
                <div className="ui-dropdown-divider" />
                <div className="px-3 py-1.5 text-[10px] text-gray-400">
                  Выбор становится действием по умолчанию
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: filters */}
        <div className="flex items-center gap-1.5 pb-2.5 flex-wrap">

          {/* Сортировка */}
          <div className="relative">
            <button
              onClick={() => toggle("sort")}
              className={`flex items-center gap-1 px-2.5 text-xs h-5 border rounded-md hover:bg-gray-50 transition-colors ${sortKey ? "border-transparent" : "border-gray-200 text-gray-600"}`}
              style={sortKey ? { background: "var(--chip-bg)", color: "var(--chip-color)", borderColor: "transparent" } : {}}
            >
              {activeSortLabel ? activeSortLabel : "Сортировка"}
              {sortKey && (
                <span className="text-[14px] leading-none">{sortDir === "asc" ? "▴" : "▾"}</span>
              )}
              {!sortKey && <span className="text-gray-400 text-[14px]">▾</span>}
            </button>
            {sortDropdown.mounted && (
              <div className="ui-popover-panel ui-popover-panel-left ui-dropdown-panel absolute top-full left-0 mt-1 min-w-[180px] z-50" data-state={sortDropdown.state}>
                {/* Dynamic entry if sorted by a column not in predefined list */}
                {sortKey && !activeSort && (
                  <>
                    <button onClick={() => setSort(sortKey, sortDir === "desc" ? "asc" : "desc")}
                      className="ui-dropdown-item font-semibold justify-between"
                      style={{ color: "var(--chip-color)" }}>
                      <span>{activeSortLabel}</span>
                      <span className="flex items-center leading-none text-[9px] gap-0.5">
                        <span style={{ opacity: sortDir === "asc" ? 1 : 0.25, fontWeight: sortDir === "asc" ? 700 : 400 }}>▲</span>
                        <span style={{ opacity: sortDir === "desc" ? 1 : 0.25, fontWeight: sortDir === "desc" ? 700 : 400 }}>▼</span>
                      </span>
                    </button>
                    <div className="ui-dropdown-divider" />
                  </>
                )}
                {SORT_OPTIONS.map(o => {
                  const isActive = activeSort?.key === o.key;
                  return (
                    <button key={o.key ?? "null"} onClick={() => {
                      if (o.key === null) { setSort(null, "desc"); setOpenDropdown(null); return; }
                      if (isActive) { setSort(o.key, sortDir === "desc" ? "asc" : "desc"); }
                      else { setSort(o.key, o.defaultDir); }
                    }}
                      className={`ui-dropdown-item justify-between ${isActive ? "font-semibold" : ""}`}
                      style={isActive ? { color: "var(--chip-color)" } : {}}>
                      <span>{o.label}</span>
                      {o.key !== null && (
                        <span className="flex items-center leading-none text-[9px] gap-0.5">
                          <span style={{ opacity: isActive && sortDir === "asc" ? 1 : 0.25, fontWeight: isActive && sortDir === "asc" ? 700 : 400 }}>▲</span>
                          <span style={{ opacity: isActive && sortDir === "desc" ? 1 : 0.25, fontWeight: isActive && sortDir === "desc" ? 700 : 400 }}>▼</span>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {showStoreFilter && storeScopeControl && (
            <div className="relative">
              <button
                onClick={() => toggle("stores")}
                className={`flex max-w-[220px] items-center gap-1 rounded-md border px-2.5 text-xs h-5 transition-colors ${storeScopeControl.storeScope !== "current" ? "border-transparent" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                style={storeScopeControl.storeScope !== "current" ? { background: "var(--chip-bg)", color: "var(--chip-color)", borderColor: "transparent" } : {}}
                title={storeScopeButtonLabel}
              >
                <span className="truncate">{storeScopeButtonLabel}</span>
                <span className="text-gray-400 text-[14px] leading-none">▾</span>
              </button>
              {storesDropdown.mounted && (
                <div className="ui-popover-panel ui-popover-panel-left ui-dropdown-panel absolute top-full left-0 mt-1 w-48 z-50" data-state={storesDropdown.state}>
                  <div className="border-b border-gray-100 px-3 py-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Магазины страницы</div>
                    <div className="mt-0.5 text-[11px] leading-snug text-gray-500">{storeScopeModeLabel}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { storeScopeControl.onChange("current", []); setOpenDropdown(null); }}
                    className={`ui-dropdown-item justify-between ${storeScopeControl.storeScope === "current" ? "font-semibold" : ""}`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate">Текущий магазин</span>
                      <span className="block truncate text-[10px] font-normal text-gray-400">как внизу слева</span>
                    </span>
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] ${storeScopeControl.storeScope === "current" ? "border-emerald-500 bg-emerald-500 text-white" : "border-gray-200 text-transparent"}`}>✓</span>
                  </button>
                  <div className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Выбрать вручную</div>
                  <div className="max-h-56 overflow-y-auto pb-1">
                    {stores.map(store => {
                      const checked = effectiveStoreSet.has(store.id);
                      return (
                        <button
                          key={store.id}
                          type="button"
                          onClick={() => toggleHeaderStore(store.id)}
                          className={`ui-dropdown-item justify-between ${checked ? "font-semibold" : ""}`}
                        >
                          <span className="truncate">{store.name}</span>
                          <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] ${checked ? "border-emerald-500 bg-emerald-500 text-white" : "border-gray-200 text-transparent"}`}>✓</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Quick attr dropdowns: Сезон, Бренд */}
          {QUICK_ATTR.map(attrKey => {
            const opt = ATTR_FILTER_OPTIONS.find(o => o.value === attrKey)!;
            return (
              <AttrDropdown
                key={attrKey}
                label={opt.label}
                selected={attrFilters[attrKey] ?? []}
                values={attrValues[attrKey] ?? []}
                isOpen={openDropdown === attrKey}
                onToggle={() => toggle(attrKey)}
                onToggleValue={val => toggleAttrValue(attrKey, val)}
              />
            );
          })}

          {promoFilterVisible && (
            <AttrDropdown
              label="Акции"
              selected={promoFilter}
              values={["Участвую", "Доступны", "Недоступно"]}
              isOpen={openDropdown === "promo"}
              onToggle={() => toggle("promo")}
              onToggleValue={val => {
                const next = promoFilter.includes(val) ? promoFilter.filter(v => v !== val) : [...promoFilter, val];
                setPromoFilter(next);
              }}
            />
          )}

          {/* Active chips: quick attrs */}
          {QUICK_ATTR.flatMap(k => (attrFilters[k] ?? []).map(v => (
            <span key={`${k}-${v}`} className="flex items-center gap-0.5 px-2 py-0.5 text-xs" style={{ background: "var(--chip-bg)", color: "var(--chip-color)", borderRadius: "0.375rem" }}>
              {v}
              <button onClick={() => toggleAttrValue(k, v)} className="ml-0.5 font-bold text-sm leading-none pb-px">×</button>
            </span>
          )))}

          {/* Active chips: extra attrs */}
          {extraAttrChips.map(({ key, val, label }) => (
            <span key={`${key}-${val}`} className="flex items-center gap-0.5 px-2 py-0.5 text-xs" style={{ background: "var(--chip-bg)", color: "var(--chip-color)", borderRadius: "0.375rem" }}>
              {label}
              <button onClick={() => toggleAttrValue(key, val)} className="ml-0.5 font-bold text-sm leading-none pb-px">×</button>
            </span>
          ))}

          {/* Active chips: numeric filters */}
          {numChips.map(({ key, label }) => (
            <span key={key} className="flex items-center gap-0.5 px-2 py-0.5 text-xs" style={{ background: "var(--chip-bg)", color: "var(--chip-color)", borderRadius: "0.375rem" }}>
              {label}
              <button onClick={() => clearNumFilter(key)} className="ml-0.5 font-bold text-sm leading-none pb-px">×</button>
            </span>
          ))}

          {/* Active chips: column filters */}
          {columnChips.map(({ key, label }) => (
            <span key={key} className="flex items-center gap-0.5 px-2 py-0.5 text-xs" style={{ background: "var(--chip-bg)", color: "var(--chip-color)", borderRadius: "0.375rem" }}>
              {label}
              <button onClick={() => clearColumnFilter(key)} className="ml-0.5 font-bold text-sm leading-none pb-px">×</button>
            </span>
          ))}

          {noteOfferIds.size > 0 && (
            <button
              onClick={() => setHasNoteFilter(!hasNoteFilter)}
              className={`flex items-center gap-1 px-2.5 text-xs h-5 border rounded-md transition-colors ${hasNoteFilter ? "border-amber-400 text-amber-700 bg-amber-50" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              Есть заметки
            </button>
          )}
          <button
            onClick={() => { const next = !hasFavFilter; setHasFavFilter(next); onFavFilterToggle.current?.(next); }}
            className={`flex items-center gap-1 px-2.5 text-xs h-5 border rounded-md transition-colors ${hasFavFilter ? "border-yellow-400 text-yellow-600 bg-yellow-50" : "border-gray-200 text-gray-500 hover:bg-gray-50"}`}
          >
            <span className={hasFavFilter ? "text-yellow-400" : "text-gray-300"}>★</span> Избранные
          </button>

          {hasFilters && (
            <button onClick={clearAllFilters} className="px-2 py-0.5 text-xs text-gray-400 hover:text-gray-600">
              Сбросить
            </button>
          )}

          {/* + Фильтр */}
          <div className="relative flex items-center gap-1.5">
            <button
              onClick={() => toggle("filterPanel")}
              className="flex items-center gap-1 px-2.5 text-xs h-5 text-gray-500 border border-dashed border-gray-300 rounded-md hover:bg-gray-50"
            >
              + Фильтр
            </button>
            {hasFilters && filteredCount !== null && totalCount !== null && filteredCount !== totalCount && (
              <span className="text-xs text-gray-400">{filteredCount} из {totalCount}</span>
            )}
            {filterPanelDropdown.mounted && (
              <FilterPanel
                attrValues={attrValues}
                attrFilters={attrFilters}
                numFilters={numFilters}
                onToggleAttrValue={toggleAttrValue}
                onSetNumFilter={setNumFilter}
                onClearNumFilter={clearNumFilter}
                exitState={filterPanelDropdown.state}
              />
            )}
          </div>

          {/* 30 дней */}
          <div className="relative ml-auto">
            <button
              type="button"
              onClick={() => setPeriodHintOpen(true)}
              className="flex items-center gap-1 px-2.5 text-xs h-5 text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50"
            >
              30 дней <span className="text-gray-400 text-[14px]">▾</span>
            </button>
            {periodHint.mounted && (
              <div className="ui-popover-panel ui-dropdown-panel absolute right-0 top-full z-50 mt-1 whitespace-nowrap px-2.5 py-1.5 text-xs font-semibold" data-state={periodHint.state}>
                В разработке
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
